import React, { Fragment, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import { Alert, Snackbar } from '@mui/material';
import { useCurrencyCodes } from 'hooks/useCurrencyCodes';
import { useCountries } from 'hooks/useCountries';
import { useLanguages } from 'hooks/useLanguages';
import { useCountryCodes } from 'hooks/useCountryCodes';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { TravelDetailSection } from 'components/travel/TravelDetailSection';
import { cloneFormItem, createEmptyFormItems } from './utils/formHelpers';
import { FIELD_RENDERERS } from './fields';
import DaySchedule from './DaySchedule';
import useTripImageAttachments from './useTripImageAttachments';
import usePackageImageAttachments from './usePackageImageAttachments';

const resolveMatchedSub = (field, currentValue) => {
    if (!field?.subFields?.length) return null;
    return field.subFields.find((sub) => {
        if (sub.showWhen === true) return currentValue === true;
        if (sub.showWhen === '__has_value__') {
            return Array.isArray(currentValue) ? currentValue.length > 0 : !!currentValue;
        }
        return sub.showWhen === currentValue;
    }) || null;
};

/**
 * Config-driven trip wizard form shell.
 * Field UIs live in ./fields; itinerary day builder in DaySchedule.
 */
const StepForm = ({
    tripId,
    ensureTripId,
    initialImageUrl,
    pendingImagePreviewUrl,
    onPendingImageSelect,
    onPendingImageClear,
    stepConfig,
    formData,
    onChange,
    errors,
    setErrors,
    paymentTemplates,
    hssTemplates,
    onToggleTemplate,
    onNewTemplate,
    onEditTemplate,
    basePrice,
    duration,
    itineraryDays,
    onDayChange,
    onAddLineItem,
    onLineItemChange,
    onRemoveLineItem,
    tripComponents,
    itineraryDaysList,
    cityOptions,
    isCitiesLoading,
    tripCountry,
    tripCountries,
    takenCountries = [],
    onNewComponent,
    onNewHssTemplate,
    onSearchExistingComponent,
    tripCurrency,
    lookupOptions,
    itemDefaults,
    tripsList,
    isTripsLoading,
    packageId,
    packageInitialImageUrl,
    pendingPackageImagePreviewUrl,
    onPendingPackageImageSelect,
    onPendingPackageImageClear,
}) => {
    const { currencies: geoCurrencies, toDropdownOptions } = useCurrencyCodes();
    // Prefer live geography master currencies (falls back to static list until loaded).
    const availableCurrencies = useMemo(
        () => toDropdownOptions(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [geoCurrencies, toDropdownOptions]
    );
    const { countries } = useCountries();
    const { languageOptions } = useLanguages();
    const { countryCodes, getByDialCode } = useCountryCodes();
    const safeFormData = formData || {};
    const safeLookupOptions = lookupOptions || {};
    const safeItemDefaults = itemDefaults || {};

    const {
        user,
        axiosInstance,
        isAttachmentModalOpen,
        setIsAttachmentModalOpen,
        imagePreviewUrl,
        snackbar,
        setSnackbar,
        handleSnackbarClose,
        fetchAttachments,
        getImageAttachment,
        handleDeleteAttachment,
    } = useTripImageAttachments({ tripId, initialImageUrl });

    const {
        isAttachmentModalOpen: isPackageAttachmentModalOpen,
        setIsAttachmentModalOpen: setIsPackageAttachmentModalOpen,
        imagePreviewUrl: packageImagePreviewUrl,
        snackbar: packageSnackbar,
        handleSnackbarClose: handlePackageSnackbarClose,
        fetchAttachments: fetchPackageAttachments,
        getImageAttachment: getPackageImageAttachment,
        handleDeleteAttachment: handleDeletePackageAttachment,
    } = usePackageImageAttachments({ packageId, initialImageUrl: packageInitialImageUrl });

    const countryOptions = useMemo(
        () => countries.map((name) => ({ value: name, label: name })),
        [countries]
    );

    const resolveLookupOptions = (field) => {
        const source = field?.optionsSource;
        if (!source) return field?.options || [];
        return safeLookupOptions[source] || field?.options || [];
    };

    const resolveFieldOptions = (field) => {
        if (field.optionsSource === 'countries') return countryOptions;
        if (field.optionsSource === 'languages') return languageOptions;
        return field.options || [];
    };

    useEffect(() => {
        return () => {
            const heroImage = safeFormData?.hero_image;
            if (heroImage && typeof heroImage === 'string' && heroImage.startsWith('blob:')) {
                URL.revokeObjectURL(heroImage);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        Object.entries(stepConfig?.fields || {}).forEach(([key, field]) => {
            // Do not auto-pad lists that use emptyMessage ("add at least one") —
            // those should stay empty until the user adds an item.
            if (field.type === 'repeatable-form' && field.minItems && !field.emptyMessage) {
                const current = safeFormData[key] || [];
                if (current.length < field.minItems) {
                    const padded = [
                        ...current.map(cloneFormItem),
                        ...createEmptyFormItems(field.minItems - current.length, field.fields),
                    ];
                    onChange(key, padded);
                }
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepConfig]);

    const renderField = (field, localData, localOnChange, keyPrefix = '', extraFieldProps = {}) => {
        const data = localData ?? safeFormData;
        const change = localOnChange ?? onChange;

        const errorKey = keyPrefix ? `${keyPrefix}.${field.key}` : field.key;
        const hasError = errors.some((e) => e.key === errorKey);
        const errorMessage = errors.find((e) => e.key === errorKey)?.message;
        const currentValue = data[field.key];

        // pull subDepth out for local layout use, keep the rest to forward
        const { subDepth: rawSubDepth, ...restExtraFieldProps } = extraFieldProps;
        const subDepth = rawSubDepth || 0;

        const matchedSub = resolveMatchedSub(field, currentValue);
        // fullWidth subFields are rendered by renderRows below the whole row
        const inlineSub = matchedSub && !matchedSub.fullWidth ? matchedSub : null;

        const Renderer = FIELD_RENDERERS[field.type];
        if (!Renderer) return null;

        if (field.useTripCurrency) {
            return null;
        }

        const isPackageImage = field.attachmentScope === 'package';

        const fieldProps = {
            field,
            data,
            change,
            hasError,
            errorMessage,
            errors,
            keyPrefix,
            matchedSub: inlineSub,
            renderRows,
            setErrors,
            stepConfig,
            basePrice,
            countries,
            countryCodes,
            getByDialCode,
            resolveFieldOptions,
            availableCurrencies,
            cityOptions,
            isCitiesLoading,
            tripCountry,
            tripCountries,
            takenCountries,
            paymentTemplates,
            onToggleTemplate,
            onNewTemplate,
            onEditTemplate,
            hssTemplates,
            onNewHssTemplate,
            tripComponents,
            onNewComponent,
            onSearchExistingComponent,
            tripCurrency,
            itineraryDaysList,
            resolveLookupOptions,
            itemDefaults: safeItemDefaults,
            ensureTripId,
            axiosInstance,
            user,
            isAttachmentModalOpen: isPackageImage ? isPackageAttachmentModalOpen : isAttachmentModalOpen,
            setIsAttachmentModalOpen: isPackageImage ? setIsPackageAttachmentModalOpen : setIsAttachmentModalOpen,
            imagePreviewUrl: isPackageImage ? packageImagePreviewUrl : imagePreviewUrl,
            getImageAttachment: isPackageImage ? getPackageImageAttachment : getImageAttachment,
            handleDeleteAttachment: isPackageImage ? handleDeletePackageAttachment : handleDeleteAttachment,
            fetchAttachments: isPackageImage ? fetchPackageAttachments : fetchAttachments,
            setSnackbar,
            pendingPreviewUrl: isPackageImage ? pendingPackageImagePreviewUrl : pendingImagePreviewUrl,
            onPendingImageSelect: isPackageImage ? onPendingPackageImageSelect : onPendingImageSelect,
            onPendingImageClear: isPackageImage ? onPendingPackageImageClear : onPendingImageClear,
            editorKey: tripId || '',
            tripId,
            tripsList,
            isTripsLoading,
            ...restExtraFieldProps,
        };

        return (
            <Box data-trip-field={errorKey}>
                <Renderer {...fieldProps} />

                {inlineSub && field.type !== 'checkbox' && (
                    subDepth === 0 ? (
                        <MDBox
                            sx={{
                                mt: 2,
                                p: 2,
                                borderLeft: '3px solid',
                                borderColor: '#1565C8',
                                borderRadius: 1,
                                bgcolor: 'rgba(21, 101, 200, 0.04)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            {(() => {
                                const matchedOption = field.options?.find(
                                    (opt) => String(opt.value) === String(currentValue)
                                );
                                return matchedOption ? (
                                    <MDBox
                                        sx={{
                                            alignSelf: 'flex-start',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 5,
                                            bgcolor: '#D6E3FF',
                                        }}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="medium"
                                            sx={{ color: '#1565C8', fontSize: '0.7rem', lineHeight: 1 }}
                                        >
                                            {matchedOption.label}
                                        </MDTypography>
                                    </MDBox>
                                ) : null;
                            })()}
                            {renderRows(
                                inlineSub.rows,
                                inlineSub.fields,
                                data,
                                change,
                                keyPrefix,
                                { subDepth: subDepth + 1 }
                            )}
                        </MDBox>
                    ) : (
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {renderRows(
                                inlineSub.rows,
                                inlineSub.fields,
                                data,
                                change,
                                keyPrefix,
                                { subDepth: subDepth + 1 }
                            )}
                        </Box>
                    )
                )}
            </Box>
        );
    };

    const renderFullWidthSub = (field, matchedSub, data, change, keyPrefix, subDepth) => {
        const currentValue = data[field.key];
        const matchedOption = field.options?.find(
            (opt) => String(opt.value) === String(currentValue)
        );
        return (
            <MDBox
                sx={{
                    mt: 0,
                    p: 2,
                    borderLeft: '3px solid',
                    borderColor: '#1565C8',
                    borderRadius: 1,
                    bgcolor: 'rgba(21, 101, 200, 0.04)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    width: '100%',
                    minWidth: 0,
                }}
            >
                {matchedOption ? (
                    <MDBox
                        sx={{
                            alignSelf: 'flex-start',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 5,
                            bgcolor: '#D6E3FF',
                        }}
                    >
                        <MDTypography
                            variant="caption"
                            fontWeight="medium"
                            sx={{ color: '#1565C8', fontSize: '0.7rem', lineHeight: 1 }}
                        >
                            {matchedOption.label}
                        </MDTypography>
                    </MDBox>
                ) : null}
                {renderRows(
                    matchedSub.rows,
                    matchedSub.fields,
                    data,
                    change,
                    keyPrefix,
                    { subDepth: (subDepth || 0) + 1 }
                )}
            </MDBox>
        );
    };

    const renderRows = (rows, fieldsMap, localData, localOnChange, keyPrefix = '', extraFieldProps = {}) => (
        rows.map((row, rowIndex) => {
            const data = localData ?? safeFormData;
            const change = localOnChange ?? onChange;
            const subDepth = extraFieldProps?.subDepth || 0;
            const fullWidthSubs = [];

            row.forEach((fieldKey) => {
                const field = { key: fieldKey, ...fieldsMap[fieldKey] };
                if (field.type === 'checkbox') return;
                const matchedSub = resolveMatchedSub(field, data[field.key]);
                if (matchedSub?.fullWidth) {
                    fullWidthSubs.push(
                        <Box key={`${fieldKey}-fullwidth-sub`} sx={{ width: '100%', minWidth: 0 }}>
                            {renderFullWidthSub(field, matchedSub, data, change, keyPrefix, subDepth)}
                        </Box>
                    );
                }
            });

            return (
                <Fragment key={rowIndex}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: row.length === 1 ? '1fr' : `repeat(${Math.min(row.length, 3)}, minmax(0, 1fr))`,
                            },
                            gap: 2,
                        }}
                    >
                        {row.map((fieldKey) => {
                            const field = { key: fieldKey, ...fieldsMap[fieldKey] };
                            return (
                                <Box key={fieldKey} sx={{ minWidth: 0 }}>
                                    {renderField(field, localData, localOnChange, keyPrefix, extraFieldProps)}
                                </Box>
                            );
                        })}
                    </Box>
                    {fullWidthSubs.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                            {fullWidthSubs}
                        </Box>
                    )}
                </Fragment>
            );
        })
    );

    const isRepeatableOnlyBox = (box) => {
        if (!box?.rows || box.rows.length !== 1 || box.rows[0].length !== 1) return false;
        const fieldKey = box.rows[0][0];
        const fieldType = stepConfig.fields?.[fieldKey]?.type;
        // key-highlights uses the same card + title layout as Hero Image
        return fieldType === 'repeatable-form';
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <MDBox>
                <MDTypography variant="h6" fontWeight="medium">{stepConfig?.title}</MDTypography>
                <MDTypography variant="body2" color="text">{stepConfig?.description}</MDTypography>
            </MDBox>

            {tripCurrency && stepConfig?.title !== 'Pricing & Billing' && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                    All prices on this trip use <strong>{tripCurrency.toUpperCase()}</strong> (set in Pricing &amp; Billing).
                </Alert>
            )}

            {(stepConfig?.boxes || []).map((box, i) => {
                const boxContent = (
                    <>
                        {box.description && (
                            <MDTypography variant="caption" color="text" display="block" mb={2}>
                                {box.description}
                            </MDTypography>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {renderRows(box.rows, stepConfig.fields)}
                        </Box>
                    </>
                );

                if (!box.title || isRepeatableOnlyBox(box)) {
                    return <Box key={i}>{boxContent}</Box>;
                }

                if (!TravelDetailSection) {
                    return <Box key={i}>{boxContent}</Box>;
                }

                return (
                    <TravelDetailSection key={i} title={box.title} mb={0}>
                        {boxContent}
                    </TravelDetailSection>
                );
            })}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Snackbar
                open={packageSnackbar.open}
                autoHideDuration={6000}
                onClose={handlePackageSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handlePackageSnackbarClose} severity={packageSnackbar.severity} sx={{ width: '100%' }}>
                    {packageSnackbar.message}
                </Alert>
            </Snackbar>

            {stepConfig?.title === 'Itinerary Builder' && DaySchedule && (
                <DaySchedule
                    duration={duration}
                    itineraryDays={itineraryDays}
                    onDayChange={onDayChange}
                    onAddLineItem={onAddLineItem}
                    onLineItemChange={onLineItemChange}
                    onRemoveLineItem={onRemoveLineItem}
                    stepConfig={stepConfig}
                    errors={errors}
                    renderRows={renderRows}
                />
            )}
        </Box>
    );
};

export default StepForm;
